import type { AnalysisPlatform } from '@/shared/api/types/generated';
import { isVestiaireAnalysis } from '@/shared/utils/analysis/platform';

/**
 * Intention d'une recherche, dérivée du scope de son URL Vinted.
 *
 * - `discovery` : périmètre large (catégorie seule ou marque seule) — l'utilisateur
 *   cherche QUOI acheter → on l'accueille sur les niches.
 * - `targeted` : périmètre ciblé (marque + catégorie, ou mots-clés/modèle) —
 *   l'utilisateur évalue un segment précis → on l'accueille sur les ventes réelles.
 *
 * Les filtres de raffinement (prix, état, taille…) ne changent pas l'intention :
 * ils précisent le périmètre, pas la question posée.
 */
export type SearchIntent = 'discovery' | 'targeted';

const hasIds = (filter: Record<string, unknown>, key: string): boolean => {
  const value = filter[key];
  return Array.isArray(value) && value.length > 0;
};

/**
 * Intention d'une analyse VESTIAIRE, lue dans son `vestiaireFilter` (jamais dans `pageUrl`,
 * qui ne porte au plus que les mots-clés). Même règle que Vinted : mot-clé ou modèle ⇒
 * ciblée ; catégorie (tout niveau, univers compris) ET marque ⇒ ciblée ; sinon découverte.
 * Filtre absent (analyse de profil) ⇒ ciblée, comme une analyse vendeur Vinted.
 */
export const getVestiaireIntent = (filter: Record<string, unknown> | null | undefined): SearchIntent => {
  if (!filter) return 'targeted';
  if (hasIds(filter, 'keywords') || hasIds(filter, 'modelIds')) return 'targeted';
  const hasCategory = ['universeIds', 'catalogRootIds', 'catalogLevel1Ids', 'catalogLevel2Ids'].some((key) =>
    hasIds(filter, key),
  );
  if (hasCategory && hasIds(filter, 'brandIds')) return 'targeted';
  return 'discovery';
};

export const getSearchIntent = (pageUrl: string | null | undefined): SearchIntent => {
  if (!pageUrl) return 'targeted';
  try {
    const url = new URL(pageUrl);
    // Les analyses vendeur (/member/…) ne sont pas des recherches catalogue :
    // on les traite comme ciblées (atterrissage sur les ventes).
    if (!url.pathname.includes('/catalog')) return 'targeted';

    const params = url.searchParams;
    const hasCatalog = params.getAll('catalog[]').length > 0;
    const hasBrand = params.getAll('brand_ids[]').length > 0 || params.getAll('brand_id[]').length > 0;
    const hasSearchText = Boolean(params.get('search_text')?.trim());

    if (hasSearchText) return 'targeted';
    if (hasCatalog && hasBrand) return 'targeted';
    return 'discovery';
  } catch {
    return 'targeted';
  }
};

export interface AnalysisIntentSource {
  pageUrl?: string | null;
  parentAnalysisId?: string | null;
  parsedFilter?: Record<string, unknown> | null;
  platform?: AnalysisPlatform | null;
  vestiaireFilter?: Record<string, unknown> | null;
}

/**
 * Intention au niveau de l'ANALYSE : un zoom (recherche fille) ou une
 * recherche par similarité (image / produit source / description) est par
 * nature un périmètre déjà resserré → toujours ciblée (atterrissage sur
 * l'historique des ventes), quelle que soit la forme de son URL.
 */
export const getAnalysisIntent = (analysis: AnalysisIntentSource | null | undefined): SearchIntent => {
  if (!analysis) return 'targeted';
  if (analysis.parentAnalysisId) return 'targeted';
  if (isVestiaireAnalysis(analysis)) return getVestiaireIntent(analysis.vestiaireFilter);
  const pf = analysis.parsedFilter;
  if (pf && (pf.imageKey || pf.sourceProductId || pf.description)) return 'targeted';
  return getSearchIntent(analysis.pageUrl);
};
